import * as React from "react";

import { AIAgentThreadContext } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { ContextType } from "@/AgentThread/AgentThreadSidebar/contextType";
import { ClientViewingContext } from "@/shared/domain/agentThread/AgentThreadRecord";

// Shared by the reactive `context` field and the `getContextSnapshot` stub so
// the two stay reference-equal, mirroring the real provider's behavior when
// no visualizer state is present.
const EMPTY_CLIENT_VIEWING_CONTEXT: ClientViewingContext = {
  dataset_ids: [],
  file_ids: [],
  collection_ids: [],
  device_ids: [],
};

/**
 * Provides a minimal, read-only AIAgentThreadContext for rendering AgentThreadByIdView
 * outside of the normal thread sidebar (e.g. in admin views).
 *
 * Most mutation handlers are no-ops and interactive features are disabled.
 * ``navigateToThread`` is the one exception: ``ForkAgentThreadButton``
 * calls it on a successful fork to navigate the viewer to the new thread,
 * so the parent can pass ``onAgentThreadClicked`` to receive that signal.
 */
interface ReadOnlyAIAgentThreadProviderProps {
  children: React.ReactNode;
  /** Called when a child requests navigation to a different thread (today,
   *  only ``ForkAgentThreadButton`` after a successful fork). When omitted the
   *  request is dropped silently. */
  onAgentThreadClicked?: (threadId: string) => void;
  /** Forwarded into context so message rows can render an admin-only debug
   *  control under each message. */
  renderMessageDebugControl?: (messageSequenceNum: number) => React.ReactNode;
  /** Forwarded into context so user-message rows can render an admin-only
   *  knowledge-graph control. */
  renderMessageKnowledgeGraphControl?: (
    messageSequenceNum: number,
  ) => React.ReactNode;
  /** Forwarded into context so AgentThreadByIdView can auto-expand the
   *  concluded-task section a scroll target lands in. */
  scrollToMessageSequenceNum?: number;
}

export const ReadOnlyAIAgentThreadProvider: React.FC<
  ReadOnlyAIAgentThreadProviderProps
> = ({
  children,
  onAgentThreadClicked,
  renderMessageDebugControl,
  renderMessageKnowledgeGraphControl,
  scrollToMessageSequenceNum,
}) => {
  const noop = React.useCallback(() => {}, []);

  const navigateToThread = React.useCallback(
    (threadId: string | undefined) => {
      // A read-only surface has no address for "a new, unsaved thread" — you
      // cannot start one from a conversation you may only look at. Only a
      // request for a real thread (today, a fork) means anything here.
      if (threadId === undefined) {
        return;
      }
      onAgentThreadClicked?.(threadId);
    },
    [onAgentThreadClicked],
  );

  const value = React.useMemo(
    () => ({
      autoEnabled: false,
      currentThreadId: undefined,
      context: EMPTY_CLIENT_VIEWING_CONTEXT,
      contextDisplayNames: { datasets: {}, files: {}, collections: {} },
      modelProfile: undefined,

      setAutoEnabled: noop as (value: boolean) => void,
      setModelProfile: noop as (profile: string) => void,

      handleContextDelete: noop as (
        contextType: ContextType,
        contextContent: string,
      ) => void,
      getContextSnapshot: () => EMPTY_CLIENT_VIEWING_CONTEXT,
      handleNewThread: noop,
      handleToggleAuto: noop,
      navigateToThread,

      renderMessageDebugControl,
      renderMessageKnowledgeGraphControl,
      scrollToMessageSequenceNum,
    }),
    [
      noop,
      navigateToThread,
      renderMessageDebugControl,
      renderMessageKnowledgeGraphControl,
      scrollToMessageSequenceNum,
    ],
  );

  return (
    <AIAgentThreadContext.Provider value={value}>
      {children}
    </AIAgentThreadContext.Provider>
  );
};
